export type RosterSheet = { name: string; rows: string[][] };

export class RosterFileError extends Error {
  constructor(readonly code: "fileType" | "size" | "empty" | "read" | "cell") {
    super(`roster-file-${code}`);
  }
}

function cellText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value).trim();
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "object") {
    const record = value as { text?: string; richText?: { text: string }[]; result?: unknown };
    if (typeof record.text === "string") return record.text.trim();
    if (Array.isArray(record.richText))
      return record.richText
        .map((part) => part.text)
        .join("")
        .trim();
    if (record.result !== undefined) return cellText(record.result);
  }
  throw new RosterFileError("cell");
}

export async function readRosterCsv(file: File): Promise<string> {
  if (!/\.csv$/i.test(file.name)) throw new RosterFileError("fileType");
  if (file.size > 10 * 1024 * 1024) throw new RosterFileError("size");
  try {
    return new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(await file.arrayBuffer());
  } catch {
    throw new RosterFileError("read");
  }
}

export async function readRosterWorkbook(file: File): Promise<RosterSheet[]> {
  if (!/\.xlsx$/i.test(file.name)) throw new RosterFileError("fileType");
  if (file.size > 10 * 1024 * 1024) throw new RosterFileError("size");
  const ExcelJS = (await import("exceljs")).default;
  const workbook = new ExcelJS.Workbook();
  try {
    await workbook.xlsx.load((await file.arrayBuffer()) as never);
  } catch {
    throw new RosterFileError("read");
  }
  const sheets: RosterSheet[] = [];
  let cells = 0;
  for (const sheet of workbook.worksheets) {
    if (sheet.rowCount > 5002 || sheet.columnCount > 80) throw new RosterFileError("size");
    const rows: string[][] = Array.from({ length: sheet.rowCount }, () => []);
    sheet.eachRow((row, number) => {
      row.eachCell((cell, column) => {
        if (cell.isMerged && cell.master.address !== cell.address) return;
        if (++cells > 50000) throw new RosterFileError("size");
        const values = rows[number - 1];
        while (values.length < column) values.push("");
        values[column - 1] = cellText(cell.value);
      });
    });
    if (rows.some((row) => row.some(Boolean))) sheets.push({ name: sheet.name, rows });
  }
  if (!sheets.length) throw new RosterFileError("empty");
  return sheets;
}
